import { useCallback, useState } from "react";
import type RealtimeKitClient from "@cloudflare/realtimekit";
import { Volume2, X } from "lucide-react";
import Swal from "sweetalert2";
import { ensureMobileSpeakerOutput } from "./mobileSpeakerRouting";

type MobileLowAudioBannerProps = {
    meetingClient: RealtimeKitClient | null;
    mobileOS: "ios" | "android" | "other";
    /** Quando true, exibe o banner (qualquer aba da sala no mobile). */
    visible?: boolean;
};

const MobileLowAudioBanner: React.FC<MobileLowAudioBannerProps> = ({
    meetingClient,
    mobileOS,
    visible = true,
}) => {
    const [dismissed, setDismissed] = useState(false);
    const [busy, setBusy] = useState(false);

    const handleBoost = useCallback(async () => {
        if (!meetingClient || busy) {
            return;
        }

        setBusy(true);
        try {
            const result = await ensureMobileSpeakerOutput(meetingClient, {
                iosMicPulse: mobileOS === "ios" || mobileOS === "android",
            });

            await Swal.fire({
                toast: true,
                position: "top",
                icon: result.ok ? "success" : "warning",
                title: result.ok ? "Áudio ajustado" : "Ainda com problema de áudio",
                text: result.message,
                showConfirmButton: false,
                timer: result.ok ? 3500 : 5500,
                timerProgressBar: true,
            });
        } finally {
            setBusy(false);
        }
    }, [busy, meetingClient, mobileOS]);

    if (
        dismissed ||
        !meetingClient ||
        !visible ||
        (mobileOS !== "ios" && mobileOS !== "android")
    ) {
        return null;
    }

    return (
        <div
            role="status"
            aria-live="polite"
            className="pointer-events-auto absolute bottom-[calc(7.5rem+env(safe-area-inset-bottom,0px))] left-3 right-3 z-50 mx-auto flex max-w-md items-center gap-2 rounded-xl border border-amber-400/50 bg-slate-900/95 px-3 py-3 text-white shadow-lg backdrop-blur-sm"
        >
            <Volume2 className="h-5 w-5 shrink-0 text-amber-300" aria-hidden />
            <p className="min-w-0 flex-1 text-xs leading-snug text-slate-100">
                Sem som ou áudio baixo? No celular o áudio pode ir para o auricular ou ficar
                bloqueado. Toque em{" "}
                <strong className="font-semibold text-white">Ouvir alto</strong>.
            </p>
            <button
                type="button"
                onClick={() => void handleBoost()}
                disabled={busy}
                className="shrink-0 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-bold text-slate-900 disabled:opacity-60"
            >
                {busy ? "…" : "Ouvir alto"}
            </button>
            <button
                type="button"
                onClick={() => setDismissed(true)}
                className="shrink-0 rounded p-0.5 text-slate-500 hover:text-slate-300"
                aria-label="Fechar aviso de áudio"
            >
                <X className="h-3.5 w-3.5" />
            </button>
        </div>
    );
};

export default MobileLowAudioBanner;
